import { useCallback, useLayoutEffect, useRef } from "react"
import type { UnknownAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { usePanelId } from "../context/panel-id"
import type { TemplateChartSettings, WithTemplateCharts } from "../store/template-chart-settings"

type TemplateChartAction = (args: {
  dashboardId: string
  panelId: string
  templateKey: string
  settings: TemplateChartSettings
}) => UnknownAction

export interface CreateTemplateChartHookConfig<S extends WithTemplateCharts> {
  // Panel-id fallback when rendered outside a PanelIdProvider (matches the slice's preference base).
  preferenceBase: string
  selectPanel: (state: ReduxState, dashboardId: string, panelKey: string) => S | undefined
  setTemplateChartSetting: TemplateChartAction
  seedTemplateChart: TemplateChartAction
}

export interface TemplateChartResult {
  selectedCategory: string | null
  selectedMetricDisplay: string | null
  // The panel slice's flag (not usePreferences') — folded template settings load with the panel,
  // so the panel gate is authoritative. Consumed by useCategorySync to defer its reset until the
  // saved category has loaded.
  isPreferencesLoaded: boolean
  setSelectedCategory: (selectedCategory: string) => void
  setSelectedMetricDisplay: (selectedMetricDisplay: string | null) => void
}

/**
 * Builds a per-template chart-settings reader for one panel slice. Pure (no preference sync — the
 * panel wrapper already syncs once), so it's safe in every header/chart consumer and in Storybook.
 *
 * Defaults differ per template and are passed by the caller; `seed` (optional) sets the initial
 * value once — e.g. an asset-view drilldown — via an existence-guarded reducer so a saved pref wins.
 */
export function createTemplateChartHook<S extends WithTemplateCharts>(
  config: CreateTemplateChartHookConfig<S>,
) {
  const { preferenceBase, selectPanel, setTemplateChartSetting, seedTemplateChart } = config

  return function useTemplateChart(
    templateKey: string,
    defaults: TemplateChartSettings,
    seed?: TemplateChartSettings | null,
  ): TemplateChartResult {
    const dashboardId = useDashboardId()
    const panelId = usePanelId() ?? preferenceBase
    const dispatch = useAppDispatch()
    const panelState = useAppSelector((s) => selectPanel(s, dashboardId, panelId))
    const settings = panelState?.templateCharts[templateKey]

    // Seed once. Layout effect for parity with the panel-config seeds; the reducer is existence-
    // guarded, so a saved/loaded preference wins and a remount never re-applies the seed.
    const seeded = useRef(false)
    useLayoutEffect(() => {
      if (seeded.current || seed == null) return
      seeded.current = true
      dispatch(seedTemplateChart({ dashboardId, panelId, templateKey, settings: seed }))
    }, [dispatch, dashboardId, panelId, templateKey, seed])

    const setSelectedCategory = useCallback(
      (selectedCategory: string) =>
        dispatch(setTemplateChartSetting({ dashboardId, panelId, templateKey, settings: { selectedCategory } })),
      [dispatch, dashboardId, panelId, templateKey],
    )
    const setSelectedMetricDisplay = useCallback(
      (selectedMetricDisplay: string | null) =>
        dispatch(setTemplateChartSetting({ dashboardId, panelId, templateKey, settings: { selectedMetricDisplay } })),
      [dispatch, dashboardId, panelId, templateKey],
    )
    // Fall back to the default only when the field is ABSENT — a stored null is meaningful (e.g. an
    // asset-view drilldown seeds selectedMetricDisplay: null to request the dual-column view), so it
    // must survive rather than coalesce back to the default.
    return {
      selectedCategory: settings?.selectedCategory !== undefined ? settings.selectedCategory : (defaults.selectedCategory ?? null),
      selectedMetricDisplay: settings?.selectedMetricDisplay !== undefined ? settings.selectedMetricDisplay : (defaults.selectedMetricDisplay ?? null),
      isPreferencesLoaded: panelState?.isPreferencesLoaded ?? false,
      setSelectedCategory,
      setSelectedMetricDisplay,
    }
  }
}
